import React, { useEffect, useMemo, useState } from "react";
import { Search, TrendingUp, Loader2, Download, FileSpreadsheet } from "lucide-react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { leadsService } from "../services/leadsService";
import { catalogService } from "../services/catalogService";
import { useOptionalAppStore } from "../store/useAppStore";
import { PageHeader } from "./PageHeader";
import { PageShell } from "./PageShell";
import { GlassCard } from "./GlassCard";
import { LeadCard } from "./LeadCard";
import { EmptyState } from "./EmptyState";
import { downloadTableExcel } from "../utils/exportTable";
import { markLabel } from "../utils/contactMarks";
import { SkeletonList } from "./SkeletonList";
import type { Lead, LeadStatus, Representative } from "../types";

const STATUS_FILTERS: Array<{ value: LeadStatus | "all"; labelKey: string }> = [
  { value: "all", labelKey: "leads.filter_all" },
  { value: "new", labelKey: "leads.filter_new" },
  { value: "contacted", labelKey: "leads.filter_contacted" },
  { value: "in_progress", labelKey: "leads.filter_in_progress" },
  { value: "qualified", labelKey: "leads.filter_qualified" },
  { value: "closed", labelKey: "leads.filter_closed" },
];

const LEAD_STATUS_LABEL_KEY: Record<string, string> = {
  new: "leads.status_new",
  contacted: "leads.status_contacted",
  in_progress: "leads.status_in_progress",
  qualified: "leads.status_qualified",
  closed: "leads.status_closed",
  rejected: "leads.status_rejected",
};

type AssigneeFilter = "all" | "unassigned" | "mine" | string;

export interface LeadsListViewProps {
  eventId: string;
  companyId: string;
  basePath: string;
  audience?: "visitor" | "admin";
  showBack?: boolean;
  /** Fallback when history is empty (gen admin → карточка экспонента). */
  backTo?: string;
  /** Filter CRM list: inbound = «Вас сканировали», outbound = «Вы сканировали» */
  direction?: "inbound" | "outbound" | "all";
  title?: string;
  subtitle?: string;
}

export function LeadsListView({
  eventId,
  companyId,
  basePath,
  audience = "admin",
  showBack = true,
  backTo,
  direction = "all",
  title,
  subtitle,
}: LeadsListViewProps) {
  const { t, i18n } = useTranslation();
  const setNavbarVisible = useOptionalAppStore()?.setNavbarVisible ?? (() => {});
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all">("all");
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [reps, setReps] = useState<Representative[]>([]);
  const [myAssigneeId, setMyAssigneeId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"csv" | "excel" | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listResult, repList] = await Promise.all([
          leadsService.listByCompany(eventId, companyId, audience, direction),
          catalogService.listRepresentatives(eventId, companyId, audience).catch(() => []),
        ]);
        if (!cancelled) {
          setAllLeads(listResult.leads);
          setMyAssigneeId(listResult.myAssigneeId);
          setReps(repList);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId, audience, direction]);

  const resolvedAssigneeId = useMemo(() => {
    if (assigneeFilter === "all") return undefined;
    if (assigneeFilter === "unassigned") return "unassigned" as const;
    if (assigneeFilter === "mine") return myAssigneeId;
    return assigneeFilter;
  }, [assigneeFilter, myAssigneeId]);

  const filteredLeads = useMemo(() => {
    let result = allLeads;

    if (resolvedAssigneeId === "unassigned") {
      result = result.filter((l) => !l.assignedTo);
    } else if (resolvedAssigneeId) {
      result = result.filter((l) => l.assignedTo === resolvedAssigneeId);
    }

    if (statusFilter !== "all") {
      result = result.filter((l) => l.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          l.visitorName.toLowerCase().includes(query) ||
          l.visitorCompany?.toLowerCase().includes(query) ||
          l.visitorPhone?.toLowerCase().includes(query) ||
          l.assignedRepName?.toLowerCase().includes(query) ||
          l.notes?.toLowerCase().includes(query) ||
          l.tags?.some(
            (tag) =>
              tag.toLowerCase().includes(query) ||
              markLabel(tag, t).toLowerCase().includes(query),
          ),
      );
    }

    return result.sort(
      (a, b) => new Date(b.scannedAt).getTime() - new Date(a.scannedAt).getTime(),
    );
  }, [allLeads, statusFilter, searchQuery, resolvedAssigneeId, t]);

  const statsByStatus = useMemo(() => {
    const base =
      resolvedAssigneeId === "unassigned"
        ? allLeads.filter((l) => !l.assignedTo)
        : resolvedAssigneeId
          ? allLeads.filter((l) => l.assignedTo === resolvedAssigneeId)
          : allLeads;
    const stats: Record<string, number> = { all: base.length };
    STATUS_FILTERS.forEach((filter) => {
      if (filter.value !== "all") {
        stats[filter.value] = base.filter((l) => l.status === filter.value).length;
      }
    });
    return stats;
  }, [allLeads, resolvedAssigneeId]);

  const leadRows = (leads: Lead[]) =>
    leads.map((l) => [
      l.visitorName,
      l.visitorPhone ?? "",
      l.visitorEmail ?? "",
      l.visitorCompany ?? "",
      l.visitorPosition ?? "",
      l.visitorCity ?? "",
      l.visitorActivityType ?? "",
      l.visitorInterestedProducts ?? "",
      l.assignedRepName ?? "",
      LEAD_STATUS_LABEL_KEY[l.status] ? t(LEAD_STATUS_LABEL_KEY[l.status]) : l.status,
      l.scanType,
      l.leadSource ?? "",
      new Date(l.scannedAt).toLocaleString(i18n.language),
      l.notes ?? "",
      (l.tags ?? []).map((tag) => markLabel(tag, t)).join("; "),
      l.rating ?? "",
    ]);

  const handleExportCsv = async () => {
    setExporting("csv");
    try {
      await leadsService.exportCsv(eventId, companyId, audience, {
        direction,
        assignedTo: resolvedAssigneeId,
      });
      toast.success(t("leads.export_csv_ok"));
    } catch {
      toast.error(t("leads.export_fail"));
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = () => {
    setExporting("excel");
    try {
      const headers = [
        t("leads.col_visitor"),
        t("leads.col_phone"),
        t("leads.col_email"),
        t("leads.col_company"),
        t("leads.col_position"),
        t("leads.col_city"),
        t("leads.col_activity_type"),
        t("leads.col_interested_products"),
        t("leads.col_assignee"),
        t("leads.col_status"),
        t("leads.col_scan_type"),
        t("leads.col_lead_source"),
        t("leads.col_scanned_at"),
        t("leads.col_notes"),
        t("leads.col_tags"),
        t("leads.col_rating"),
      ];
      downloadTableExcel(
        `leads-${eventId}-${companyId}`,
        headers,
        leadRows(filteredLeads),
        t("leads.excel_sheet_name"),
      );
      toast.success(t("leads.export_excel_ok"));
    } catch {
      toast.error(t("leads.export_excel_fail"));
    } finally {
      setExporting(null);
    }
  };

  const hasActiveFilters =
    searchQuery || statusFilter !== "all" || assigneeFilter !== "all";

  return (
    <>
      <PageHeader
        title={
          title ??
          (direction === "outbound" ? t("leads.title_outbound") : t("leads.title_inbound"))
        }
        subtitle={
          subtitle ?? t("leads.list_subtitle", { n: filteredLeads.length })
        }
        back={showBack}
        backTo={backTo}
        right={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void handleExportCsv()}
              disabled={!!exporting || loading}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
              title="CSV"
            >
              {exporting === "csv" ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Download size={15} />
              )}
              CSV
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={!!exporting || loading || filteredLeads.length === 0}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
              title="Excel"
            >
              {exporting === "excel" ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <FileSpreadsheet size={15} />
              )}
              Excel
            </button>
          </div>
        }
      />
      <PageShell>
        <div className="mt-4 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <GlassCard variant="default" padding="sm">
              <div className="flex items-center gap-2 text-gray-400">
                <Search size={18} />
                <input
                  type="text"
                  placeholder={t("leads.search_placeholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setNavbarVisible(false)}
                  onBlur={() => setNavbarVisible(true)}
                  className="flex-1 bg-transparent border-none outline-none text-[14px] text-gray-900 placeholder:text-gray-400"
                />
              </div>
            </GlassCard>
          </motion.div>

          {(reps.length > 0 || myAssigneeId) && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03, duration: 0.3 }}
            >
              <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4">
                <button
                  type="button"
                  onClick={() => setAssigneeFilter("all")}
                  className={`px-3 py-1.5 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all ${
                    assigneeFilter === "all"
                      ? "bg-slate-800 text-white"
                      : "bg-white text-gray-600 border border-gray-200"
                  }`}
                >
                  {t("leads.filter_assignee_all")}
                </button>
                {myAssigneeId ? (
                  <button
                    type="button"
                    onClick={() => setAssigneeFilter("mine")}
                    className={`px-3 py-1.5 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all ${
                      assigneeFilter === "mine"
                        ? "bg-slate-800 text-white"
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                  >
                    {t("leads.filter_assignee_mine")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setAssigneeFilter("unassigned")}
                  className={`px-3 py-1.5 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all ${
                    assigneeFilter === "unassigned"
                      ? "bg-slate-800 text-white"
                      : "bg-white text-gray-600 border border-gray-200"
                  }`}
                >
                  {t("leads.filter_assignee_none")}
                </button>
                {reps.map((rep) => (
                  <button
                    key={rep.id}
                    type="button"
                    onClick={() => setAssigneeFilter(rep.id)}
                    className={`px-3 py-1.5 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all max-w-[160px] truncate ${
                      assigneeFilter === rep.id
                        ? "bg-slate-800 text-white"
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                    title={rep.fullName}
                  >
                    {rep.fullName}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.3 }}
          >
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium whitespace-nowrap transition-all ${
                    statusFilter === filter.value
                      ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                      : "bg-white text-gray-600 border border-gray-200 hover:border-indigo-200"
                  }`}
                >
                  {t(filter.labelKey)}
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[11px] ${
                      statusFilter === filter.value
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {statsByStatus[filter.value] || 0}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>

          {loading ? (
            <SkeletonList count={6} variant="row" />
          ) : filteredLeads.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="space-y-2"
            >
              {filteredLeads.map((lead, i) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  <LeadCard lead={lead} basePath={basePath} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <EmptyState
              icon={<TrendingUp size={32} />}
              title={
                hasActiveFilters ? t("leads.not_found_title") : t("leads.empty_title")
              }
              description={
                hasActiveFilters ? t("leads.not_found_desc") : t("leads.empty_desc")
              }
            />
          )}
        </div>
      </PageShell>
    </>
  );
}
